import { computed } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppMultiSelectProps } from '../types/interfaces'
import type { UseAppSelectCoreReturn } from '../../app-select'

import { useAppSelectCore } from '../../app-select'
import { createMultiSelectionAdapter } from './create-multi-selection-adapter'
import { useSelectAll } from './use-select-all.hook'

interface UseAppMultiSelectOptions<TValue = string | number> {
  modelValue: Ref<TValue[] | undefined>
  props: AppMultiSelectProps<TValue>
  onOpen: () => void
  onClose: () => void
}

interface UseAppMultiSelectReturn<TValue = string | number>
  extends UseAppSelectCoreReturn<TValue> {
  inputValue: ComputedRef<string>
  inputPlaceholder: ComputedRef<string>
  displayedOptions: ComputedRef<AppSelectOption<TValue>[]>
  selectedValues: ComputedRef<TValue[]>
  isOptionSelected: (option: AppSelectOption<TValue>) => boolean
  /** True when every enabled visible option is in the model (and there is at
   *  least one enabled visible option). */
  isAllSelected: ComputedRef<boolean>
  /** True when there are no enabled visible options to toggle. */
  isSelectAllDisabled: ComputedRef<boolean>
  /** Toggle all enabled visible options in the model. */
  toggleSelectAll: () => void
}

export const useAppMultiSelect = <TValue = string | number>(
  options: UseAppMultiSelectOptions<TValue>,
): UseAppMultiSelectReturn<TValue> => {
  const { modelValue, props, onOpen, onClose } = options

  const selectedValues = computed<TValue[]>(() => modelValue.value ?? [])

  const displayedOptions = computed<AppSelectOption<TValue>[]>(() => props.options)

  const selection = createMultiSelectionAdapter<TValue>({ modelValue, selectedValues })
  // Reuse the adapter's predicate for the template / return surface.
  const isOptionSelected = selection.isSelected

  const { isAllSelected, isSelectAllDisabled, toggleSelectAll } = useSelectAll<TValue>({
    modelValue,
    selectedValues,
    displayedOptions,
  })

  const inputValue = computed<string>(() =>
    props.options
      .filter(o => selectedValues.value.includes(o.value))
      .map(o => o.text)
      .join(', '),
  )

  const inputPlaceholder = computed<string>(() => {
    if (selectedValues.value.length > 0) return ''
    return props.placeholder ?? ''
  })

  const core = useAppSelectCore<TValue>({
    props,
    displayedOptions,
    mode: { inputEditable: false },
    selection,
    onOpen,
    onClose,
  })

  return {
    ...core,
    inputValue,
    inputPlaceholder,
    displayedOptions,
    selectedValues,
    isOptionSelected,
    isAllSelected,
    isSelectAllDisabled,
    toggleSelectAll,
  }
}
